/**
 * Stato di un ordine del negozio come lo mostra il negozio stesso (segnalazione Magazzino 23/10/2026):
 * - WooCommerce: un'etichetta (orders.external_financial_status = stato grezzo, es. processing, wc-prepared);
 * - Shopify: due etichette come nel suo pannello, pagamento (displayFinancialStatus, in
 *   external_financial_status) ed evasione (displayFulfillmentStatus, in external_fulfillment_status).
 * Gli ordini manuali non ne hanno: mostrano lo stato di Flusia. Lo stato di Flusia (orders.status) resta
 * per tutti e guida magazzino, preparazione e DDT.
 * Valori Shopify: shopify.dev/docs/api/admin-graphql/latest/enums/OrderDisplayFinancialStatus e
 * .../OrderDisplayFulfillmentStatus.
 */
import type { OrderChannel } from '../types/orders';

export type StoreStatusTone = 'gray' | 'blue' | 'green' | 'amber' | 'red' | 'purple';

export interface StoreStatusBadge {
  label: string;
  tone: StoreStatusTone;
}

/** Etichette di WooCommerce in italiano; prepared/shipped sono gli stati del plugin Flusia. */
const WOO: Readonly<Record<string, StoreStatusBadge>> = Object.freeze({
  pending: { label: 'In attesa di pagamento', tone: 'gray' },
  processing: { label: 'In lavorazione', tone: 'green' },
  'on-hold': { label: 'In attesa', tone: 'amber' },
  prepared: { label: 'Preparato', tone: 'purple' },
  shipped: { label: 'Spedito', tone: 'blue' },
  completed: { label: 'Completato', tone: 'blue' },
  cancelled: { label: 'Annullato', tone: 'gray' },
  refunded: { label: 'Rimborsato', tone: 'gray' },
  failed: { label: 'Fallito', tone: 'red' },
  'checkout-draft': { label: 'Bozza', tone: 'gray' },
  trash: { label: 'Cestinato', tone: 'gray' },
});

const SHOPIFY_PAYMENT: Readonly<Record<string, StoreStatusBadge>> = Object.freeze({
  PENDING: { label: 'Pagamento in sospeso', tone: 'amber' },
  AUTHORIZED: { label: 'Autorizzato', tone: 'amber' },
  PARTIALLY_PAID: { label: 'Parzialmente pagato', tone: 'amber' },
  PAID: { label: 'Pagato', tone: 'gray' },
  PARTIALLY_REFUNDED: { label: 'Parzialmente rimborsato', tone: 'gray' },
  REFUNDED: { label: 'Rimborsato', tone: 'gray' },
  VOIDED: { label: 'Pagamento annullato', tone: 'gray' },
  EXPIRED: { label: 'Scaduto', tone: 'red' },
});

const SHOPIFY_FULFILLMENT: Readonly<Record<string, StoreStatusBadge>> = Object.freeze({
  UNFULFILLED: { label: 'Non evaso', tone: 'amber' },
  OPEN: { label: 'Non evaso', tone: 'amber' },
  RESTOCKED: { label: 'Non evaso', tone: 'amber' },
  PARTIALLY_FULFILLED: { label: 'Parzialmente evaso', tone: 'amber' },
  IN_PROGRESS: { label: 'In corso', tone: 'blue' },
  PENDING_FULFILLMENT: { label: 'In corso', tone: 'blue' },
  ON_HOLD: { label: 'In sospeso', tone: 'amber' },
  SCHEDULED: { label: 'Programmato', tone: 'blue' },
  FULFILLED: { label: 'Evaso', tone: 'gray' },
  FULFILLMENT_NOT_REQUIRED: { label: 'Evasione non richiesta', tone: 'gray' },
  REQUEST_DECLINED: { label: 'Richiesta rifiutata', tone: 'red' },
});

/** Classi Tailwind per tono (stesso stile delle pillole di stato di Flusia). */
export const STORE_STATUS_TONE_CLASS: Readonly<Record<StoreStatusTone, string>> = Object.freeze({
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
});

/** Stato personalizzato di un altro plugin: lo slug leggibile ("attesa-bonifico" -> "Attesa bonifico"). */
function humanize(slug: string): string {
  const t = slug.replace(/[-_]+/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : slug;
}

export interface StoreStatusInput {
  channel: OrderChannel | string | null | undefined;
  /** orders.external_financial_status (WooCommerce: stato grezzo; Shopify: pagamento). */
  rawFinancial?: string | null;
  /** orders.external_fulfillment_status (solo Shopify). */
  rawFulfillment?: string | null;
  /** orders.status: per Shopify serve a dire "Annullato" (lo stato di pagamento non lo dice). */
  status?: string | null;
}

/** Etichette dello stato nel negozio; null per gli ordini manuali o senza dato del negozio. */
export function storeStatusBadges(i: StoreStatusInput): StoreStatusBadge[] | null {
  if (i.channel === 'woocommerce') {
    const raw = i.rawFinancial?.trim().replace(/^wc-/, '');
    if (!raw) return null;
    return [WOO[raw] ?? { label: humanize(raw), tone: 'gray' }];
  }
  if (i.channel === 'shopify') {
    const pay = i.rawFinancial?.trim().toUpperCase();
    const ful = i.rawFulfillment?.trim().toUpperCase();
    const out: StoreStatusBadge[] = [];
    if (i.status === 'cancelled') out.push({ label: 'Annullato', tone: 'gray' });
    if (pay) out.push(SHOPIFY_PAYMENT[pay] ?? { label: humanize(pay.toLowerCase()), tone: 'gray' });
    if (ful) out.push(SHOPIFY_FULFILLMENT[ful] ?? { label: humanize(ful.toLowerCase()), tone: 'gray' });
    return out.length ? out : null;
  }
  return null;
}
